import { Observable, of } from 'rxjs'
import { filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators'
import { authSelectors } from 'src/app/core/auth/auth.selectors'
import { AppState } from 'src/app/core/core.state'
import { Member } from 'src/app/features/member/member'
import { Product } from 'src/app/features/product/product'
import { productSelectors } from 'src/app/features/product/product.selectors'
import { Component, Input, OnInit } from '@angular/core'
import { Dictionary } from '@ngrx/entity'
import { Store } from '@ngrx/store'
import { Sale } from '../../sale'
import { saleSelectors } from '../../sale.selectors'
import { SaleItem } from '../sale-item'
import { saleItemSelectors } from '../sale-item.selectors'
import { Subscription } from 'src/app/features/subscription/subscription'
import { subscriptionSelectors } from 'src/app/features/subscription/subscription.selectors'
import { SaleItemService } from '../sale-item.service'
import { Document } from 'src/app/features/document/document'
import { documentSelectors } from 'src/app/features/document/document.selectors'
import { environment } from 'src/environments/environment'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { courseActions } from 'src/app/features/course/course.actions'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { Rental } from 'src/app/features/rental/rental'
import { rentalSelectors } from 'src/app/features/rental/rental.selectors'
import { BsModalService } from 'ngx-bootstrap/modal'
import { QrModalComponent } from 'src/app/shared/components/qr-modal-component/qr-modal.component'
import { Participation } from 'src/app/features/booking/participation/participation'
import { participationSelectors } from 'src/app/features/booking/participation/participation.selectors'
import { subscriberActions } from 'src/app/features/subscription/subscriber/subscriber.actions'
import { FamilyMemberService } from 'src/app/features/family/family-members/family-member.service'
@UntilDestroy()
@Component({
  selector: 'sale-item-table-card',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class SaleItemTableComponent implements OnInit {
  @Input() sale: Sale
  @Input() saleItems$: Observable<SaleItem[]>
  public paymentDefaultSiteId = environment.paymentDefaultSiteId
  public isLoading$: Observable<boolean>
  public saleItemsTotal$: Observable<number>
  public member$: Observable<Member>
  public productEntities$: Observable<Dictionary<Product>>
  public documentEntities$: Observable<Dictionary<Document>>
  public subscriptionEntities$: Observable<Dictionary<Subscription>>
  public selectedLanguage$: Observable<string>
  public staticAssetBaseUrl: string = environment.staticAssetBaseUrl
  private fetchedCourseIds = []
  public rentalEntities$: Observable<Dictionary<Rental>>
  public showQrCodeButtonGenerate: boolean = environment.showDownloadQrButton
  public saleItemBasePrices = {}
  public bookingParticipations$: Observable<Participation[]>
  public shouldShowCategories: boolean = false

  constructor(
    private store: Store<AppState>,
    public saleItemService: SaleItemService,
    private modalService: BsModalService,
    private familyMemberService: FamilyMemberService
  ) {}

  ngOnInit() {
    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )

    this.isLoading$ = this.store.select(saleSelectors.selectIsLoading)
    this.member$ = this.store.select(authSelectors.selectMember)
    this.productEntities$ = this.store.select(productSelectors.selectEntities)
    this.documentEntities$ = this.store.select(documentSelectors.selectEntities)
    this.subscriptionEntities$ = this.store.select(
      subscriptionSelectors.selectEntities
    )
    this.rentalEntities$ = this.store.select(rentalSelectors.selectEntities)

    this.saleItemsTotal$ = this.store.select(
      saleItemSelectors.selectTotal,
      this.sale.id
    )

    this.bookingParticipations$ = this.store.select(
      participationSelectors.selectAll
    )

    this.member$
      .pipe(
        filter((member) => Boolean(member)),
        take(1)
      )
      .subscribe((member) => {
        this.loadFamilyMemberSubscribers(member)
      })

    this.saleItems$
      .pipe(untilDestroyed(this), withLatestFrom(this.productEntities$))
      .subscribe(([saleItems, productEntities]) => {
        this.shouldShowCategories = Boolean(
          saleItems
            .map((s) => s.customerCategory)
            .filter((category) => category !== null)?.length ||
            saleItems.some(
              (saleItem) => saleItem?.params?.familyMembersInvited?.length
            )
        )

        if (saleItems && saleItems.length > 0) {
          saleItems.map((saleItem, idx) => {
            let fetchableCourseIds = []
            if (
              saleItem.courseId &&
              !this.fetchedCourseIds.includes(saleItem.courseId)
            ) {
              this.fetchedCourseIds.push(saleItem.courseId)
              fetchableCourseIds.push(saleItem.courseId)
            }

            if (fetchableCourseIds.length > 0) {
              this.store.dispatch(
                courseActions.fetchMany({
                  params: RequestQueryBuilder.create({
                    search: { id: { [CondOperator.IN]: fetchableCourseIds } },
                    join: { field: 'bookings' }
                  })
                })
              )
            }
          })
        }

        // do base price cacls
        const didChargeBasePriceResourceProductIds: string[] = []

        if (!productEntities) {
          return
        }
        this.saleItemBasePrices = []
        for (const saleItem of saleItems) {
          const product =
            productEntities[
              saleItem?.params?.bookableLinkedProductId ??
                saleItem?.params?.bookableProductId
            ]
          const basePriceKey = `${saleItem.params?.startDate}-${saleItem.params?.bookableProductId}`

          if (
            product &&
            product.allowAsLinkedProduct &&
            product.allowChooseCategory &&
            product.basePrice &&
            saleItem?.params?.bookableProductId &&
            !didChargeBasePriceResourceProductIds.includes(basePriceKey)
          ) {
            didChargeBasePriceResourceProductIds.push(basePriceKey)
            this.saleItemBasePrices[
              `${saleItem?.id}-${saleItem?.params?.startDate}-${saleItem?.params?.bookableProductId}`
            ] = product.basePrice
          }
        }
      })
  }

  public showQrModal(saleItem: SaleItem) {
    this.bookingParticipations$
      .pipe(
        filter((participations) =>
          saleItem.bookingId ? Boolean(participations.length) : true
        ),
        take(1),
        map(() => {
          // shitty local gov hasselt fix - this was regressed from NGS-2923
          this.modalService.show(QrModalComponent, {
            initialState: {
              claimCodes: [saleItem.claimCode],
              displayQrInModal: true
            },
            class: 'modal-sm'
          })
        })
      )
      .pipe(untilDestroyed(this))
      .subscribe()
  }

  public getSaleItemBasePrice(saleItem: SaleItem) {
    if (!saleItem && !saleItem.params?.bookableProductId) {
      return 0
    }

    const key = `${saleItem?.id}-${saleItem.params?.startDate}-${saleItem.params?.bookableProductId}`

    if (this.saleItemBasePrices[key]) {
      return this.saleItemBasePrices[key]
    }

    return 0
  }

  public participationsFetched(saleItem: SaleItem): Observable<boolean> {
    if (!this.bookingParticipations$ || !saleItem?.bookingId) {
      return of(true)
    }

    return this.bookingParticipations$.pipe(
      map((participations) => {
        return !!participations.length
      })
    )
  }

  public shouldShowBookerCategory(saleItem: SaleItem): boolean {
    return !(
      saleItem?.params?.familyMembersInvited?.length &&
      (saleItem.subscriptionId || saleItem?.params?.bookerAssisting === false)
    )
  }

  private loadFamilyMemberSubscribers(member: Member) {
    this.familyMemberService
      .isFamilyFeatureEnabled()
      .pipe(take(1))
      .subscribe((isEnabled) => {
        if (!isEnabled || !member?.id) {
          return
        }

        this.familyMemberService
          .getAll()
          .pipe(take(1))
          .subscribe((familyMembers) => {
            const familyMemberIds = familyMembers
              .filter((familyMember) => familyMember.memberId !== member.id)
              .map((familyMember) => familyMember.memberId)

            if (!familyMemberIds.length) {
              return
            }

            this.store.dispatch(
              subscriberActions.fetchActiveGlobalForMembers({
                memberIds: familyMemberIds
              })
            )
          })
      })
  }
}
